/**
 * Session Context - управление состоянием аутентификации
 *
 * Предоставляет глобальное состояние сессии для всего приложения:
 * - user: текущий пользователь (null если не авторизован)
 * - loading: состояние загрузки (проверка сессии)
 * - login(email, password, remember_me): вход
 * - logout(): выход
 * - checkSession(): проверка текущей сессии
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Проверка текущей сессии (при загрузке приложения)
  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include', // Отправить cookie
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.role === 'PARENT') {
          // Родители не имеют доступа к CRM — перенаправляем в ЛК
          window.location.href = '/cabinet/';
          return;
        }
        setUser(userData);
      } else if (response.status === 401) {
        // Сессия истекла или не существует
        setUser(null);
      } else {
        throw new Error('Failed to check session');
      }
    } catch (err) {
      console.error('Session check error:', err);
      setUser(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Вход в систему
  const login = async (email, password, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include', // Получить cookie
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          remember_me: rememberMe
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();

      if (data.user?.role === 'PARENT') {
        // Родители не имеют доступа к CRM — перенаправляем в ЛК
        window.location.href = '/cabinet/';
        return { success: true, mustChangePassword: false };
      }

      setUser(data.user);

      return {
        success: true,
        mustChangePassword: data.must_change_password,
        user: data.user
      };
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  // Выход из системы
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);

      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include', // Удалить cookie
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setUser(null);
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  // Обновление данных пользователя
  const refreshUser = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return { success: true, user: userData };
      }
      return { success: false };
    } catch (err) {
      console.error('Refresh user error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Загрузка аватара
  const uploadAvatar = async (file) => {
    try {
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/auth/avatar', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Avatar upload failed');
      }

      const data = await response.json();

      // Обновить аватар в состоянии пользователя
      if (user) {
        setUser({ ...user, avatar_url: data.avatar_url });
      }

      return { success: true, avatar_url: data.avatar_url };
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Смена пароля
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Password change failed');
      }

      const data = await response.json();

      // Обновить флаг must_change_password
      if (user) {
        setUser({ ...user, must_change_password: false });
      }

      return {
        success: true,
        message: data.message
      };
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  // Проверить сессию при загрузке приложения
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    checkSession,
    changePassword,
    refreshUser,
    uploadAvatar
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

// Хук для использования контекста
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

export default SessionContext;
